import { useMemo } from 'react';

export type DropdownItem<T> = {
  key: string;
  item: T;
  disabled: boolean;
};

type UseDropdownItemsOptions<T> = {
  allItems: T[] | null;
  getItemValue: (item: T) => string;
  isItemEnabled: (item: T) => boolean;
  getItemLabel?: (item: T) => string;
};

type FilteredItem<T> = {
  item: T;
  isDisabled: boolean;
};

export const getFilterItems = <T>(
  allItems: T[],
  isItemEnabled: (item: T) => boolean,
  getItemLabel: (item: T) => string,
): FilteredItem<T>[] => {
  if (!allItems || allItems.length === 0) return [];

  return allItems
    .map((item) => {
      return {
        item,
        isDisabled: !isItemEnabled(item),
      };
    })
    .sort((a, b) => {
      // First, enabled items come first
      if (a.isDisabled !== b.isDisabled) return a.isDisabled ? 1 : -1;

      // Then sort alphabetically
      const labelA = getItemLabel(a.item).toLowerCase();
      const labelB = getItemLabel(b.item).toLowerCase();
      return labelA.localeCompare(labelB);
    });
};

export function useDropdownItems<T>({
  allItems,
  getItemValue,
  isItemEnabled,
  getItemLabel,
}: UseDropdownItemsOptions<T>): DropdownItem<T>[] {
  return useMemo(() => {
    if (!allItems) return [];

    const uniqueItemsMap = new Map<string, T>();
    allItems.forEach((item) => {
      const value = getItemValue(item);
      if (!uniqueItemsMap.has(value)) uniqueItemsMap.set(value, item);
    });
    const uniqueItems = Array.from(uniqueItemsMap.values());

    return getFilterItems(uniqueItems, isItemEnabled, getItemLabel || getItemValue).map(
      ({ item, isDisabled }) => ({
        key: getItemValue(item),
        item: getItemLabel ? (getItemLabel(item) as T) : item,
        disabled: isDisabled,
      }),
    );
  }, [allItems, getItemValue, isItemEnabled, getItemLabel]);
}
